import type {
  HotelDetailApiResponse,
  RoomType,
  HotelListApiResponse,
  HotelListItem as HotelListItemType,
  DestinationsApiResponse,
} from "@/types/hotel";
export const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL?.replace(/\/$/, "") ||
  "https://www.lilylanka.com";

// ===================================================
// --- GENERIC TYPES ---
// ===================================================

export interface ApiError {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  errors?: Record<string, string[]>;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T | ApiError;
  status?: number;
}

// ===================================================
// --- AUTH HELPER ---
// ===================================================

async function fetchWithAuth(
  url: string,
  token: string,
  options: RequestInit = {},
) {
  const headers = new Headers(options.headers || {});

  // Only set Authorization header if token exists and is not empty
  if (token && token.trim().length > 0) {
    headers.set("Authorization", `Bearer ${token}`);
  } else {
    // This log is from your 'hotel-manage' branch, kept for debugging
    console.log("🔍 Making anonymous request - auth system under development");
  }

  if (options.body) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");

  // Debug logging for submit requests
  if (url.includes("/submit")) {
    console.log("🔍 Request Debug:");
    console.log("URL:", url);
    console.log("Method:", options.method);
    console.log("Headers:", Object.fromEntries(headers.entries()));
    console.log("Body:", options.body);
    console.log("Token present:", !!token);
    console.log("Token length:", token?.length || 0);
  }

  return fetch(url, { ...options, headers });
}

// ===================================================
// --- AUTH FUNCTIONS (Login, Check Email, etc.) ---
// ===================================================

// POST /api/Auth/login
export interface LoginPayload {
  email: string;
  password: string;
}
export interface LoginResponse {
  partnerId: number;
  token: string;
  status: string;
}
export async function login(
  payload: LoginPayload,
): Promise<ApiResponse<LoginResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Auth/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    // Kept safer error handling
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as LoginResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Login API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/Auth/check-email
export interface CheckEmailPayload {
  email: string;
}
export interface CheckEmailResponse {
  exists: boolean;
  status: string;
  emailVerified: boolean;
  requiresMfa: boolean;
}
export async function checkEmail(
  payload: CheckEmailPayload,
): Promise<ApiResponse<CheckEmailResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Auth/check-email`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (response.ok) {
      return {
        success: true,
        data: data as CheckEmailResponse,
        status: response.status,
      };
    } else {
      return {
        success: false,
        data: data as ApiError,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Check Email API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/PartnerAuth/send-email-confirmation-link
export async function sendPartnerEmailConfirmationLink(
  payload: SendEmailConfirmationRequest,
): Promise<ApiResponse<SendEmailConfirmationResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/PartnerAuth/send-email-confirmation-link`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as SendEmailConfirmationResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Send Partner Email Confirmation API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/PartnerAuth/confirm-email
export async function confirmPartnerEmail(
  email: string,
  token: string,
): Promise<ApiResponse<ConfirmEmailResponse>> {
  try {
    const queryParams = new URLSearchParams({ email, token });

    const response = await fetch(
      `${API_BASE_URL}/api/PartnerAuth/confirm-email?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as ConfirmEmailResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Confirm Partner Email API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/Auth/request-password-reset
export interface RequestPasswordResetPayload {
  email: string;
}
export interface RequestPasswordResetResponse {
  status: string;
  message: string;
}
export async function requestPasswordReset(
  payload: RequestPasswordResetPayload,
): Promise<ApiResponse<RequestPasswordResetResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Auth/request-password-reset`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      let data;
      try {
        data = await response.json();
      } catch {
        data = { status: "Success", message: "Request received." };
      }
      return {
        success: true,
        data: data as RequestPasswordResetResponse,
        status: response.status,
      };
    }

    const errorData = await response.json();
    return {
      success: false,
      data: errorData as ApiError,
      status: response.status,
    };
  } catch (error) {
    console.error("Request Password Reset API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/Auth/reset-password
export interface ResetPasswordPayload {
  token: string;
  email: string;
  newPassword: string;
}
export interface ResetPasswordResponse {
  status: string;
  message: string;
}
export async function resetPassword(
  payload: ResetPasswordPayload,
): Promise<ApiResponse<ResetPasswordResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Auth/reset-password`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (response.ok) {
      return {
        success: true,
        data: data as ResetPasswordResponse,
        status: response.status,
      };
    } else {
      return {
        success: false,
        data: data as ApiError,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Reset Password API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- PARTNER FUNCTIONS (Register) ---
// ===================================================

// POST /api/Partner/register-partner
export interface RegisterPartnerPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
  moduleCode: "PROPERTY" | "EXPERIENCE" | "TOUR_GUIDE" | "TRANSPORT";
}
export interface RegisterPartnerResponse {
  partnerId: number;
  emailVerificationToken: string;
}
export async function registerPartner(
  payload: RegisterPartnerPayload,
): Promise<ApiResponse<RegisterPartnerResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Partner/register-partner`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    const data = await response.json();
    if (response.status === 201) {
      // Use 201 for Created
      return {
        success: true,
        data: data as RegisterPartnerResponse,
        status: response.status,
      };
    } else {
      return {
        success: false,
        data: data as ApiError,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Registration API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/Auth/send-email-confirmation-link
export interface SendEmailConfirmationRequest {
  email: string;
}

export interface SendEmailConfirmationResponse {
  sent: boolean;
  expiresAt: string;
}

export async function sendEmailConfirmationLink(
  payload: SendEmailConfirmationRequest,
): Promise<ApiResponse<SendEmailConfirmationResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Auth/send-email-confirmation-link`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as SendEmailConfirmationResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Send Email Confirmation API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/Auth/confirm-email
export interface ConfirmEmailResponse {
  verified: boolean;
}

export async function confirmEmail(
  email: string,
  token: string,
): Promise<ApiResponse<ConfirmEmailResponse>> {
  try {
    const queryParams = new URLSearchParams({
      email: email,
      token: token,
    });

    const response = await fetch(
      `${API_BASE_URL}/api/Auth/confirm-email?${queryParams.toString()}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as ConfirmEmailResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Confirm Email API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
// POST /api/Partner/forgot-password
export interface PartnerForgotPasswordPayload {
  email: string;
  baseResetUrl: string;
}
export interface PartnerForgotPasswordResponse {
  status: string;
  message: string;
}
export async function partnerForgotPassword(
  payload: PartnerForgotPasswordPayload,
): Promise<ApiResponse<PartnerForgotPasswordResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Partner/forgot-password`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      let data;
      try {
        data = await response.json();
      } catch {
        data = { status: "Success", message: "Request received." };
      }
      return {
        success: true,
        data: data as PartnerForgotPasswordResponse,
        status: response.status,
      };
    }

    const errorData = await response.json();
    return {
      success: false,
      data: errorData as ApiError,
      status: response.status,
    };
  } catch (error) {
    console.error("Partner Forgot Password API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/Partner/reset-password
export interface PartnerResetPasswordPayload {
  email: string;
  token: string;
  newPassword: string;
}
export interface PartnerResetPasswordResponse {
  status: string;
  message: string;
}
export async function partnerResetPassword(
  payload: PartnerResetPasswordPayload,
): Promise<ApiResponse<PartnerResetPasswordResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Partner/reset-password`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    const data = await response.json();
    if (response.ok) {
      return {
        success: true,
        data: data as PartnerResetPasswordResponse,
        status: response.status,
      };
    } else {
      return {
        success: false,
        data: data as ApiError,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Partner Reset Password API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}
// ===================================================
// --- UPLOAD FUNCTIONS ---
// ===================================================

export interface PresignUploadPayload {
  fileName: string;
  contentType: string;
  folder: string;
  isPublic: boolean;
  cacheControl: string;
}

export interface PresignUploadResponse {
  uploadUrl: string;
  requiredHeaders: Record<string, string>;
  objectKey: string;
  publicCdnUrl: string;
}

export async function getPresignedUploadUrlPublic(
  payload: PresignUploadPayload,
): Promise<ApiResponse<PresignUploadResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Storage/presign-upload`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();
      // // ← ADD THIS FIX HERE
      // if (data.publicCdnUrl) {
      //   data.publicCdnUrl = data.publicCdnUrl.replace(
      //     'https://assets.lilylanka.com',
      //     'https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com'
      //   );
      // }
      return {
        success: true,
        data: data as PresignUploadResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Presigned URL API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
export async function uploadToPresignedUrl(
  uploadUrl: string,
  file: File,
  requiredHeaders: Record<string, string>,
): Promise<boolean> {
  try {
    const headers = new Headers(requiredHeaders);

    // Set content type from file
    headers.set("Content-Type", file.type);

    console.log("Uploading to presigned URL:", {
      fileName: file.name,
      fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      fileType: file.type,
    });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: headers,
      body: file,
    });

    console.log("Upload response:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
    });

    return response.ok;
  } catch (error) {
    console.error("Upload to presigned URL error:", error);
    return false;
  }
}

// POST /api/Storage/presign-upload
export async function getPresignedUploadUrl(
  payload: PresignUploadPayload,
  token: string,
): Promise<ApiResponse<PresignUploadResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Storage/presign-upload`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      // ← ADD THIS FIX HERE
      // if (data.publicCdnUrl) {
      //   data.publicCdnUrl = data.publicCdnUrl.replace(
      //     'https://assets.lilylanka.com',
      //     'https://lilylanka-public-prod.sgp1.cdn.digitaloceanspaces.com'
      //   );
      // }
      return {
        success: true,
        data: data as PresignUploadResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Presigned URL API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PUT to S3/Spaces via proxy (CORS workaround)
export async function uploadFileToS3(
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  file: File,
  retries: number = 2,
): Promise<boolean> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt} for ${file.name}`);
        await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
      }

      console.log("Uploading file via proxy:", {
        fileName: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
        fileType: file.type,
        attempt: attempt + 1,
      });

      // Use Next.js API route as proxy to avoid CORS
      const formData = new FormData();
      formData.append("uploadUrl", uploadUrl);
      formData.append("file", file);
      formData.append("headers", JSON.stringify(requiredHeaders));

      const response = await fetch("/api/upload-proxy", {
        method: "POST",
        body: formData,
      });

      console.log("Proxy upload response:", {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          error: response.statusText,
        }));
        console.error("Proxy upload failed:", {
          status: response.status,
          error: errorData,
        });

        // Retry on server errors
        if (
          attempt < retries &&
          (response.status >= 500 || response.status === 408)
        ) {
          continue;
        }
      }

      return response.ok;
    } catch (error) {
      console.error(`Proxy upload error (attempt ${attempt + 1}):`, error);

      if (attempt === retries) {
        return false;
      }
    }
  }

  return false;
}

// ===================================================
// --- LOCATION FUNCTIONS ---
// ===================================================

export interface Location {
  uid: number;
  name: string;
  district: string;
  province: string;
  latitude: number;
  longitude: number;
  createdBy: number;
  creationDate: string;
  modifiedBy: number;
  modifiedDate: string;
}

export interface LocationSearchParams {
  q?: string;
  limit?: number;
  offset?: number;
}

// GET /api/Location
export async function searchLocations(
  params: LocationSearchParams,
  token: string,
): Promise<ApiResponse<Location[]>> {
  const queryParams = new URLSearchParams();
  if (params.q) queryParams.set("q", params.q);
  if (params.limit) queryParams.set("limit", String(params.limit));
  if (params.offset) queryParams.set("offset", String(params.offset));

  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Location?${queryParams.toString()}`,
      token,
      {
        method: "GET",
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as Location[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Search Locations API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface CreateLocationPayload {
  name: string;
  district: string;
  province: string;
  latitude: number;
  longitude: number;
  createdBy: number;
}

// POST /api/Location
export async function createLocation(
  payload: CreateLocationPayload,
  token: string,
): Promise<ApiResponse<Location>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Location`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as Location, status: response.status };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Create Location API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- 📍 CATALOG FUNCTIONS 📍 ---
// ===================================================

export interface CatalogItem {
  id: number;
  code: string;
  name: string;
  icon: string | null;
  description: string | null;
  level: number;
  sortOrder: number;
}

export interface CatalogItemWithChildren extends CatalogItem {
  children: CatalogItem[];
}

// GET /api/Catalog
export async function getCatalog(
  token: string,
  parentCode?: string,
  parentId?: number,
): Promise<ApiResponse<CatalogItem[]>> {
  const queryParams = new URLSearchParams();
  if (parentCode) queryParams.set("parent_code", parentCode);
  if (parentId) queryParams.set("parent_id", String(parentId));

  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Catalog?${queryParams.toString()}`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CatalogItem[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Catalog API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/Catalog/{code}
export async function getCatalogByCode(
  code: string,
  token: string,
): Promise<ApiResponse<CatalogItemWithChildren>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Catalog/${code}`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CatalogItemWithChildren,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Catalog by Code API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- PARTNER MODULES ---
// ===================================================

export interface PartnerModule {
  // moduleCode: string;|
   moduleCode?: string;
  status: string;
  active: boolean;
}

export interface PartnerModulesResponse {
  partnerId: number;
  modules: PartnerModule[];
}

export async function getPartnerModules(
  partnerId: number,
  token: string,
): Promise<ApiResponse<PartnerModulesResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Partner/${partnerId}/modules`,
      token,
      { method: "GET" },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as PartnerModulesResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Partner Modules API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- HOTEL ONBOARDING FUNCTIONS ---
// ===================================================

export interface OnboardingStepResponse {
  sessionId: number;
  currentStep: number;
  status: string;
}

// POST /api/HotelOnboarding/onboarding/start
export async function startOnboarding(
  partnerId: number,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/start`,
      token,
      {
        method: "POST",
        body: JSON.stringify({ partnerId }),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Start Onboarding API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/HotelOnboarding/lookups
export interface LookupItem {
  id: number;
  code: string;
  name: string;
  node_type: number;
}
export interface LookupsResponse {
  starRatings: number[];
  categories: LookupItem[];
  accessModes: LookupItem[];
  paymentMethods: LookupItem[];
  payoutMethods: LookupItem[];
  amenities: LookupItem[];
}

export async function getFilterLookups(): Promise<
  ApiResponse<LookupsResponse>
> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/lookup`, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: {
          starRatings: data.starRatings || [],
          categories: data.categories || [],
          accessModes: data.accessModes || [],
          paymentMethods: data.paymentMethods || [],
          payoutMethods: data.payoutMethods || [],
          amenities: data.amenities || [],
        } as LookupsResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Filter Lookups API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export async function getLookups(
  token: string,
): Promise<ApiResponse<LookupsResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/lookups`,
      token,
      {
        method: "GET",
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as LookupsResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Lookups API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/HotelOnboarding/onboarding/{sessionId}
export interface OnboardingSession {
  uid: number;
  partnerId: number;
  hotelId: number;
  currentStep: number;
  status: string;
  payload: string;
  createdDate: string;
  modifiedDate: string;
}
export async function getOnboardingSession(
  sessionId: number,
  token: string,
): Promise<ApiResponse<OnboardingSession>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}`,
      token,
      {
        method: "GET",
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingSession,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Onboarding Session API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/HotelOnboarding/onboarding/get
export async function postGetOnboardingSession(
  sessionId: number,
  token: string,
): Promise<ApiResponse<OnboardingSession>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/get`,
      token,
      {
        method: "POST",
        body: JSON.stringify({ sessionId }),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingSession,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Post Get Onboarding Session API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/basic
export interface UpdateBasicInfoPayload {
  name: string;
  isPropertyManager: boolean;
  chainName?: string;
  starRating?: number | null; 
  partnerId: number;
  parentCatalogId: number;
  subCatalogId: number;
}
export async function updateOnboardingBasic(
  sessionId: number,
  payload: UpdateBasicInfoPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/basic`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Basic Info API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/{sessionId}/map
export interface UpdateMapPayload {
  latitude: number;
  longitude: number;
}
export async function updateOnboardingMap(
  sessionId: number,
  payload: UpdateMapPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/${sessionId}/map`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      return { success: true, status: response.status }; // 200 OK with no body
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {
        /* ignore */
      }
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Map API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/address
export interface UpdateAddressPayload {
  partnerId: number;
  country: string;
  city: string;
  province: string;
  zipcode: string;
  address: string;
  locationId?: number;
}
export async function updateOnboardingAddress(
  sessionId: number,
  payload: UpdateAddressPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/address`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Address API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/details
export interface UpdateDetailsPayload {
  partnerId: number;
  checkinTime: string;
  checkoutTime: string;
  accessModes: string[];
  amenityCodes: string[];
  paymentMethods: string[];
  cancellationPolicy: string;
}
export async function updateOnboardingDetails(
  sessionId: number,
  payload: UpdateDetailsPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/details`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Details API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/rooms
export interface RoomImageDto {
  url: string;
  isCover: boolean;
  caption?: string;
  sortOrder?: number;
}
export interface RoomDraftDto {
  name: string;
  sizeSqm: number;
  occupancyAdults: number;
  occupancyKids: number;
  bathrooms: number;
  minRateLkr: number;
  quantity: number;
  // Meal plan fields
  hasBreakfast: boolean;
  breakfastPriceLkr: number;
  hasBreakfastAndDinner: boolean;
  breakfastAndDinnerPriceLkr: number;
  hasBreakfastAndLunch: boolean;
  breakfastAndLunchPriceLkr: number;
  hasAllMeals: boolean;
  allMealsPriceLkr: number;
   images: RoomImageDto[];
}

// Add after RoomDraftDto interface
export interface PaxRateDto {
  paxCount: number;
  minRateLkr: number;
  rateBreakfastLkr: number;
  rateBreakfastDinnerLkr: number;
  rateBreakfastLunchLkr: number;
  rateAllMealsLkr: number;
}

export interface BedDto {
  bedTypeId: number;
  bedTypeName: string;
  quantity: number;
}

export interface RoomDraftWithPaxDto {
  name: string;
  roomTypeMasterId: number | null;
  roomTypeMasterName: string;
  roomTypeSubId: number | null;
  roomTypeSubName: string;
  beds: BedDto[];
  sizeSqm: number;
  bathrooms: number;
  quantity: number;
  images: RoomImageDto[];
  paxRates: PaxRateDto[];
}

export interface UpdateRoomsWithPaxPayload {
  partnerId: number;
  providesBreakfast: boolean;
  rooms: RoomDraftWithPaxDto[];
}

// Add new function after updateOnboardingRooms
export async function updateOnboardingRoomsWithPaxRates(
  sessionId: number,
  payload: UpdateRoomsWithPaxPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/rooms-with-pax-rates`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Rooms With Pax Rates API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface UpdateRoomsPayload {
  partnerId: number;
  providesBreakfast: boolean;
  rooms: RoomDraftDto[];
}

export async function updateOnboardingRooms(
  sessionId: number,
  payload: UpdateRoomsPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/rooms`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Rooms API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface MasterRoomType {
  uid: number;
  name: string;
  otaWording: string;
  roomTypeLabel: string;
  roomNameLabel: string;
  roomNumberLabel: string;
  description: string;
  sortOrder: number;
}

export async function getMasterRoomTypes(): Promise<ApiResponse<MasterRoomType[]>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hotel-onboarding/room-types/master`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as MasterRoomType[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface SubRoomType {
  uid: number;
  masterRoomTypeId: number;
  name: string;
  otaWording: string;
  description: string;
  sortOrder: number;
}

export async function getSubRoomTypes(
  masterRoomTypeId: number,
): Promise<ApiResponse<SubRoomType[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/hotel-onboarding/room-types/master/${masterRoomTypeId}/sub-types`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as SubRoomType[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ADD this after the getMasterRoomTypes function:

export interface EntirePropertyRoomType {
  uid: number;
  name: string;
  otaWording?: string;       // keep for backward compat
  roomTypeSubId: number | null;
  roomIndex?: number;
  quantity?: number;
  sizeSqm?: number;
  bathrooms?: number;
}

export async function getEntirePropertyRoomTypes(
  sessionId: number,
  partnerId: number,
): Promise<ApiResponse<EntirePropertyRoomType[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/HotelOnboarding/${sessionId}/entire-property/room-types?partnerId=${partnerId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as EntirePropertyRoomType[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface BedType {
  uid: number;
  name: string;
  displayName: string;
  widthLabel: string;
  widthMinCm: number | null;
  widthMaxCm: number | null;
  occupancyDefault: number;
  occupancyMin: number;
  occupancyMax: number;
  description: string;
  sortOrder: number;
}

export async function getBedTypes(): Promise<ApiResponse<BedType[]>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/hotel-onboarding/bed-types`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as BedType[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/photos

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/photos
export interface PropertyPhotoDto {
  url: string;
  isCover: boolean;
}
export interface RoomPhotoDto {
  url: string;
  caption: string;
}
export interface UpdatePhotosPayload {
  partnerId: number;
  propertyPhotos: PropertyPhotoDto[];
  roomPhotos: RoomPhotoDto[];
}
export async function updateOnboardingPhotos(
  sessionId: number,
  payload: UpdatePhotosPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/photos`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Photos API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- ENTIRE PROPERTY ONBOARDING ---
// Add these types and function to your existing api.ts
// ===================================================

export interface EntirePropertyRoomTypeDto {
  roomTypeSubId: number;
  roomName: string;
  roomCount: number;
}
export interface UpdateEntirePropertyPayload {
  partnerId: number;
  enabled: boolean;
  propertyType: string;
  paxCount: number;
  minRateLkr: number;
  minRateUsd: number;
  bbRateLkr: number | null;          // ← allow null
  bbRateUsd: number | null;
  halfBoardRateLkr: number | null;
  halfBoardRateUsd: number | null;
  fullBoardRateLkr: number | null;
  fullBoardRateUsd: number | null;
  roomTypes: EntirePropertyRoomTypeDto[];
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/entire-property
export async function updateOnboardingEntireProperty(
  sessionId: number,
  payload: UpdateEntirePropertyPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/entire-property`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Entire Property API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PATCH /api/HotelOnboarding/onboarding/{sessionId}/account
export interface BankAccountDto {
  name: string;
  number: string;
  bank: string;
  branch: string;
  swift?: string;
}
export interface UpdateAccountPayload {
  partnerId: number;
  firstName: string;
  lastName: string;
  nationality: string;
  phone: string;
  preferredLanguage: string;
  addressInfo: {
    country: string;
    state: string;
    city: string;
  };
  payout: {
    method: string;
    bankAccount: BankAccountDto;
  };
}
export async function updateOnboardingAccount(
  sessionId: number,
  payload: UpdateAccountPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/account`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Update Account API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/HotelOnboarding/onboarding/{sessionId}/contract
export interface SignContractPayload {
  partnerId: number;
  signerName: string;
  ip: string;
}
export async function signOnboardingContract(
  sessionId: number,
  payload: SignContractPayload,
  token: string,
): Promise<ApiResponse<OnboardingStepResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/contract`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as OnboardingStepResponse,
        status: response.status,
      };
    } else {
      const responseText = await response.text();
      console.error("❌ Sign Contract Error Response (raw):", responseText);

      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = JSON.parse(responseText);
      } catch {
        console.error(
          "Contract error response is not JSON:",
          responseText.substring(0, 500),
        );
        errorData = {
          title: "Server Error",
          detail: responseText.substring(0, 200),
          status: response.status,
        };
      }
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Sign Contract API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// POST /api/HotelOnboarding/onboarding/{sessionId}/submit
export interface SubmitOnboardingResponse {
  hotelId: number;
  sessionId: number;
  status: string;
}
export async function submitOnboarding(
  sessionId: number,
  partnerId: number,
  token: string,
): Promise<ApiResponse<SubmitOnboardingResponse>> {
  try {
    if (!partnerId || partnerId === 0) {
      return {
        success: false,
        data: { title: "Partner ID is required", status: 400 },
        status: 400,
      };
    }

    // --- THIS IS THE FIX ---
    // The API expects camelCase 'partnerId', not 'PartnerId'
    const payload = { partnerId: partnerId };
    // --- END OF FIX ---

    console.log("🔍 Submit Debug Info:");
    console.log("SessionId:", sessionId);
    console.log("PartnerId:", partnerId);
    console.log("Payload:", payload);
    console.log("JSON Payload:", JSON.stringify(payload));
    console.log(
      "URL:",
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/submit`,
    );

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}/submit`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as SubmitOnboardingResponse,
        status: response.status,
      };
    } else {
      const responseText = await response.text();
      console.error("❌ Submit Onboarding Error Response (raw):", responseText);
      console.error("❌ Submit Status:", response.status, response.statusText);
      console.error(
        "❌ Submit Request - SessionId:",
        sessionId,
        "Payload:",
        payload,
      );

      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };

      try {
        const errorJson = JSON.parse(responseText);
        errorData = errorJson;
        console.error("❌ Submit Error (parsed):", errorJson);
      } catch {
        console.error(
          "Submit error response is not JSON:",
          responseText.substring(0, 500),
        );
        errorData = {
          title: "Server Error",
          detail: responseText.substring(0, 200),
          status: response.status,
        };
      }
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    // <-- Fixed: Added error parameter
    console.error("Submit Onboarding API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- HOTEL SEARCH FUNCTIONS ---
// ===================================================

export interface HotelListItem {
  id: number;
  name: string;
  location: string;
  stars: number;
  priceFrom: number;
  coverImageUrl: string;
  amenities: string[];
}

/**
 * GET /api/Hotels - Search hotels
 */
export async function getHotels(searchParams?: {
  locationId?: number;
  minStars?: number;
  search?: string;
  amenities?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<HotelListApiResponse>> {
  try {
    const params = new URLSearchParams();

    // Priority 1: Use locationId if provided (most specific)
    if (searchParams?.locationId) {
      params.append("locationId", searchParams.locationId.toString());
      console.log("🎯 Searching by locationId:", searchParams.locationId);
    }
    // Priority 2: Use text search for hotel names or general search
    else if (searchParams?.search) {
      // The API accepts 'search' parameter for searching hotels
      params.append("search", searchParams.search);
      console.log("🔍 Searching by text:", searchParams.search);
    }

    // Add other filters
    if (searchParams?.minStars) {
      params.append("minStars", searchParams.minStars.toString());
    }
    if (searchParams?.amenities) {
      params.append("amenities", searchParams.amenities);
    }
    if (searchParams?.sort) {
      params.append("sort", searchParams.sort);
    }

    // Always include pagination
    params.append("page", (searchParams?.page || 1).toString());
    params.append("pageSize", (searchParams?.pageSize || 20).toString());

    const url = `${API_BASE_URL}/api/Hotels?${params.toString()}`;
    console.log("🔍 Fetching hotels from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    console.log("📦 Hotels response status:", response.status);

    if (response.ok) {
      const data = await response.json();

      console.log("✅ Hotels data received:", {
        itemCount: data.result?.items?.length || 0,
        total: data.result?.paging?.total || 0,
        firstHotel: data.result?.items?.[0]?.name || "N/A",
      });

      return {
        success: true,
        data: data as HotelListApiResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch (e) {
        console.error("❌ Could not parse error response:", e);
      }

      console.error("❌ Hotels API error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Hotels API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

// TEST FUNCTION - Add this temporarily to test the API directly
// export async function testHotelSearch() {
//   console.log('🧪 Testing hotel search API...');

//   const testCases = [
//     { locationId: 1, desc: 'with locationId=1' },
//     { search: 'Colombo', desc: 'with search=Colombo' },
//     { name: 'Colombo', desc: 'with name=Colombo' },
//     { query: 'Colombo', desc: 'with query=Colombo' },
//     { q: 'Colombo', desc: 'with q=Colombo' },
//     { location: 'Colombo', desc: 'with location=Colombo' },
//   ];

//   for (const testCase of testCases) {
//     const { desc, ...params } = testCase;
//     const queryString = new URLSearchParams(
//       Object.entries(params).map(([k, v]) => [k, String(v)])
//     ).toString();

//     const url = `${API_BASE_URL}/api/Hotels?${queryString}&page=1&pageSize=5`;
//     console.log(`\n🧪 Testing ${desc}:`);
//     console.log(`   URL: ${url}`);

//     try {
//       const response = await fetch(url, {
//         method: 'GET',
//         headers: { 'Accept': 'application/json' },
//         cache: 'no-store',
//       });

//       const data = await response.json();
//       const count = data.result?.items?.length || 0;
//       console.log(`   ✅ Status: ${response.status}, Results: ${count}`);

//       if (count > 0) {
//         console.log(`   🎉 SUCCESS! Use parameter: ${Object.keys(params)[0]}`);
//         console.log(`   First hotel: ${data.result.items[0].name}`);
//       }
//     } catch (error) {
//       console.log(`   ❌ Failed: ${error}`);
//     }
//   }
// }

/**
 * GET /api/Hotels/{id} - Get hotel by ID
 */
export async function getHotelById(
  id: string | number,
  searchParams?: {
    checkIn?: string;
    checkOut?: string;
    adults?: number;
    children?: number;
  },
): Promise<ApiResponse<HotelDetailApiResponse>> {
  try {
    const params = new URLSearchParams();

    // Parse check-in date
    if (searchParams?.checkIn) {
      const checkInDate = new Date(searchParams.checkIn);
      if (!isNaN(checkInDate.getTime())) {
        params.append("checkIn.year", checkInDate.getFullYear().toString());
        params.append("checkIn.month", (checkInDate.getMonth() + 1).toString());
        params.append("checkIn.day", checkInDate.getDate().toString());
        params.append("checkIn.dayOfWeek", checkInDate.getDay().toString());
      }
    }

    // Parse check-out date
    if (searchParams?.checkOut) {
      const checkOutDate = new Date(searchParams.checkOut);
      if (!isNaN(checkOutDate.getTime())) {
        params.append("checkOut.year", checkOutDate.getFullYear().toString());
        params.append(
          "checkOut.month",
          (checkOutDate.getMonth() + 1).toString(),
        );
        params.append("checkOut.day", checkOutDate.getDate().toString());
        params.append("checkOut.dayOfWeek", checkOutDate.getDay().toString());
      }
    }

    // Add guest counts
    if (searchParams?.adults !== undefined) {
      params.append("adults", searchParams.adults.toString());
    }
    if (searchParams?.children !== undefined) {
      params.append("children", searchParams.children.toString());
    }

    const url = `${API_BASE_URL}/api/Hotels/${id}${
      params.toString() ? "?" + params.toString() : ""
    }`;
    console.log("🔍 Fetching hotel detail from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store", // Disable caching
    });

    console.log("📦 Hotel detail response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel detail data received:", {
        hotelId: data.hotel?.uid,
        name: data.hotel?.name,
        imagesCount: data.images?.length || 0,
        roomTypesCount: data.roomTypes?.length || 0,
      });

      return {
        success: true,
        data: data as HotelDetailApiResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError;
      try {
        errorData = await response.json();
      } catch (e) {
        console.error("❌ Could not parse error response:", e);
        errorData = {
          title: response.statusText || "Error",
          status: response.status,
          detail: `HTTP ${response.status}: ${response.statusText}`,
        };
      }
      console.error("❌ Hotel detail API error:", errorData);

      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Hotel By ID API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}
/**
 * GET /api/Location/{id} - Get location by ID (no auth required)
 */
export interface Location {
  uid: number;
  name: string;
  district: string;
  province: string;
  latitude: number;
  longitude: number;
  createdBy: number;
  creationDate: string;
  modifiedBy: number;
  modifiedDate: string;
}

export async function getLocationById(
  id: number,
  token: string,
): Promise<ApiResponse<Location>> {
  try {
    const url = `${API_BASE_URL}/api/Location/${id}`;
    console.log("🔍 Fetching location from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`, // <-- use token
      },
      cache: "no-store",
    });

    console.log("📦 Location response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Location data received:", data);
      return {
        success: true,
        data: data as Location,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch (e) {
        console.error("❌ Could not parse location error response:", e);
      }

      console.error("❌ Location API error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Location API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

// // Insurance
// export interface InsuranceRegistrationRequest {
//   firstName: string;
//   lastName: string;
//   nationality: string;
//   dateOfBirth: string; // ISO 8601 format
//   email: string;
//   passportNumber: string;
//   arrivalDate: string; // ISO 8601 format
//   departureDate: string; // ISO 8601 format
//   passportPhotoUrl: string;
//   declarationParticularsTrue: boolean;
//   declarationGoodHealth: boolean;
//   declarationNotAwareCircumstances: boolean;
//   declarationAcceptPrivacyPolicy: boolean;
//   acceptTermsAndConditions: boolean;
// }

// export interface InsuranceRegistrationResponse {
//   uid: number;
//   message: string;
// }

// export const registerInsurance = async (
//   data: InsuranceRegistrationRequest
// ): Promise<InsuranceRegistrationResponse> => {
//   const response = await fetch(
//     "https://www.lilylanka.com/api/InsuranceRegistrations/register",
//     {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         accept: "application/json",
//       },
//       body: JSON.stringify(data),
//     }
//   );

//   if (!response.ok) {
//     const errorData = await response.json();
//     throw new Error(errorData.detail || "Failed to register insurance");
//   }

//   return response.json();
// };

// export const uploadPassportPhoto = async (file: File): Promise<string> => {
//   try {
//     // Validate file type
//     const allowedTypes = ["image/jpeg", "image/png", "application/pdf"];
//     if (!allowedTypes.includes(file.type)) {
//       throw new Error("Only JPEG, PNG, and PDF files are allowed");
//     }

//     // Validate file size (max 5MB)
//     const maxSize = 5 * 1024 * 1024; // 5MB
//     if (file.size > maxSize) {
//       throw new Error("File size must be less than 5MB");
//     }

//     // Step 1: Get presigned upload URL (NO AUTH - public endpoint)
//     const presignPayload: PresignUploadPayload = {
//       fileName: file.name,
//       contentType: file.type,
//       folder: "insurance/passports",
//       isPublic: true,
//       cacheControl: "public, max-age=31536000",
//     };

//     console.log("🔍 Requesting presigned URL with payload:", presignPayload);

//     const presignResponse = await getPresignedUploadUrlPublic(presignPayload);

//     if (!presignResponse.success || !presignResponse.data) {
//       throw new Error("Failed to get presigned upload URL");
//     }

//     const { uploadUrl, requiredHeaders, publicCdnUrl } =
//       presignResponse.data as PresignUploadResponse;

//     console.log("✅ Received presigned URL");
//     console.log("📤 Starting upload via proxy...");

//     // Step 2: Upload file using the PROXY (avoids CORS)
//     const uploadSuccess = await uploadFileToS3(
//       uploadUrl,
//       requiredHeaders,
//       file,
//       2 // retries
//     );

//     if (!uploadSuccess) {
//       throw new Error("Failed to upload file to storage");
//     }

//     console.log("✅ File uploaded successfully via proxy");
//     console.log("🌐 Public URL:", publicCdnUrl);

//     // Step 3: Return the public CDN URL
//     return publicCdnUrl;
//   } catch (error) {
//     console.error("❌ Passport photo upload error:", error);
//     throw error instanceof Error
//       ? error
//       : new Error("Failed to upload passport photo");
//   }
// };

// ===================================================
// --- INSURANCE REGISTRATION WITH PAYMENT ---
// ===================================================

export interface InsuranceRegistrationRequest {
  firstName: string;
  lastName: string;
  nationality: string;
  dateOfBirth: string; // ISO 8601 format
  email: string;
  passportNumber: string;
  arrivalDate: string; // ISO 8601 format
  departureDate: string; // ISO 8601 format
  passportPhotoUrl: string;
  declarationParticularsTrue: boolean;
  declarationGoodHealth: boolean;
  declarationNotAwareCircumstances: boolean;
  declarationAcceptPrivacyPolicy: boolean;
  acceptTermsAndConditions: boolean;
  usdAmount: number;
  basicPremiumLkr: number;
  tripDays: number;
}

// ✅ UPDATED: Backend now returns payment URL directly
export interface InsuranceRegistrationResponse {
  uid: {
    insuranceRegistrationUid: number;
    paycenterReqId: string;
    paycenterPaymentPageUrl: string;
  };
  message: string;
}

/**
 * Register insurance and get PayCenter payment URL
 * Backend automatically creates payment session
 */
export const registerInsurance = async (
  data: InsuranceRegistrationRequest,
): Promise<InsuranceRegistrationResponse> => {
  const response = await fetch(
    `${API_BASE_URL}/api/InsuranceRegistrations/register`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
      },
      body: JSON.stringify(data),
    },
  );

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || "Failed to register insurance");
  }

  return response.json();
};
export interface InsurancePremiumRequest {
  arrivalDate: string; // ISO 8601
  departureDate: string; // ISO 8601
  birthDate: string; // ISO 8601
}

export interface InsurancePremiumDto {
  usdAmount: number;
  basicPremiumLkr: number;
  tripDays: number;
}

export interface ErrorDetail {
  code?: string;
  // add other fields if your ErrorDetail has them (field, description, etc.)
}

export interface BaseResponse<T> {
  result: T | null;
  message: string;
  errors: ErrorDetail[];
  id: number;
}
export interface ErrorDetail {
  code?: string;
  category?: number;
  description?: string;
  element?: string;
  elementValue?: string;
  location?: string;
}
export const getInsurancePremium = async (
  data: InsurancePremiumRequest,
): Promise<BaseResponse<InsurancePremiumDto>> => {
  const url = `${API_BASE_URL}/api/InsuranceRegistrations/premium`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
    },
    body: JSON.stringify(data),
  });

  // backend validation errors may come as ProblemDetails
  if (!response.ok) {
    let errorMessage = "Failed to calculate premium";
    try {
      const errorData = await response.json();
      errorMessage = errorData?.detail || errorData?.title || errorMessage;
    } catch {
      // ignore json parse issues
    }
    throw new Error(errorMessage);
  }

  const res = (await response.json()) as BaseResponse<InsurancePremiumDto>;

  if (res.errors?.length) {
    const msg =
      res.errors
        .map((e) => e.description)
        .filter(Boolean)
        .join("\n") ||
      res.message ||
      "Failed to calculate premium";
    throw new Error(msg);
  }

  return res;
};
// ===================================================
// --- PAYMENT RETURN/CANCEL HANDLERS ---
// ===================================================

/**
 * Handle payment return (success)
 * Called when user returns from PayCenter after successful payment
 */
export async function handlePaymentReturn(
  insuranceUid: number,
): Promise<ApiResponse<{ status: string; message: string }>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/InsuranceRegistrations/payments/return?insuranceUid=${insuranceUid}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data,
        status: response.status,
      };
    }

    const errorData = await response.json();
    return {
      success: false,
      data: errorData as ApiError,
      status: response.status,
    };
  } catch (error) {
    console.error("Handle Payment Return API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

/**
 * Handle payment cancellation
 * Called when user cancels payment at PayCenter
 */
export async function handlePaymentCancel(
  insuranceUid: number,
): Promise<ApiResponse<{ status: string; message: string }>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/InsuranceRegistrations/payments/cancel?insuranceUid=${insuranceUid}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data,
        status: response.status,
      };
    }

    const errorData = await response.json();
    return {
      success: false,
      data: errorData as ApiError,
      status: response.status,
    };
  } catch (error) {
    console.error("Handle Payment Cancel API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

/**
 * Test PayCenter integration (for development)
 */
export async function testPayCenter(): Promise<ApiResponse<any>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/InsuranceRegistrations/test-paycenter`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data,
        status: response.status,
      };
    }

    const errorData = await response.json();
    return {
      success: false,
      data: errorData as ApiError,
      status: response.status,
    };
  } catch (error) {
    console.error("Test PayCenter API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// ===================================================
// --- ROOM MANAGEMENT FUNCTIONS ---
// ===================================================

export interface RoomDto {
  roomId: number;
  hotelId: number;
  name: string;
  sizeSqm: number;
  occupancyAdults: number;
  occupancyKids: number;
  bathrooms: number;
  quantity: number;
  minRateLKR: number; // ← ADDED: This field is now returned by the API
  hasBreakfast: boolean;
  breakfastPriceLkr: number;
  hasBreakfastAndDinner: boolean;
  breakfastAndDinnerPriceLkr: number;
  hasBreakfastAndLunch: boolean;
  breakfastAndLunchPriceLkr: number;
  hasAllMeals: boolean;
  allMealsPriceLkr: number;
}

export interface CreateRoomPayload {
  partnerId: number;
  name: string;
  occupancyAdults: number;
  occupancyKids: number;
  sizeSqm: number;
  bathrooms: number;
  quantity: number;
  hasBreakfast: boolean;
  breakfastPriceLkr: number;
  hasBreakfastAndDinner: boolean;
  breakfastAndDinnerPriceLkr: number;
  hasBreakfastAndLunch: boolean;
  breakfastAndLunchPriceLkr: number;
  hasAllMeals: boolean;
  allMealsPriceLkr: number;
  minRateLkr: number;
}

export interface UpdateRoomPayload {
  partnerId: number;
  name: string;
  sizeSqm: number;
  occupancyAdults: number;
  occupancyKids: number;
  bathrooms: number;
  quantity: number;
  hasBreakfast: boolean;
  breakfastPriceLkr: number;
  minRateLkr: number;
  hasBreakfastAndDinner: boolean;
  breakfastAndDinnerPriceLkr: number;
  hasBreakfastAndLunch: boolean;
  breakfastAndLunchPriceLkr: number;
  hasAllMeals: boolean;
  allMealsPriceLkr: number;
}

// GET /api/Hotels/{hotelId}/rooms
export async function getRoomsByHotelId(
  hotelId: number,
  token: string,
): Promise<ApiResponse<RoomDto[]>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/rooms`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Rooms fetched:", data);
      return {
        success: true,
        data: data as RoomDto[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Rooms API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Hotels/{hotelId}/rooms
export async function createRoom(
  hotelId: number,
  payload: CreateRoomPayload,
  token: string,
): Promise<ApiResponse<number>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/rooms`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Room created:", data);
      return {
        success: true,
        data: data as number,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Create Room API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{roomId}
export async function updateRoom(
  roomId: number,
  hotelId: number,
  payload: UpdateRoomPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${roomId}?hotelId=${hotelId}`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.status === 204 || response.ok) {
      console.log("✅ Room updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Room API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// DELETE /api/Hotels/{roomId}
export async function deleteRoom(
  roomId: number,
  hotelId: number,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${roomId}?hotelId=${hotelId}`,
      token,
      { method: "DELETE" },
    );

    if (response.status === 204 || response.ok) {
      console.log("✅ Room deleted successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Delete Room API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
// Replace the updateOnboardingSession function in your api.ts with this:

export async function updateOnboardingSession(
  sessionId: number,
  payload: any,
  token: string,
): Promise<ApiResponse<OnboardingSession>> {
  const url = `${API_BASE_URL}/api/HotelOnboarding/onboarding/${sessionId}`;

  try {
    console.log("🔄 Updating session:", sessionId);
    console.log("📦 Payload to save:", payload);

    const response = await fetchWithAuth(url, token, {
      method: "PATCH",
      body: JSON.stringify({
        payload: JSON.stringify(payload),
      }),
    });

    console.log("📡 Update session response status:", response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Failed to update session:", errorText);
      return {
        success: false,
        data: {
          title: "Update Session Error",
          detail:
            errorText || `Failed to update session: ${response.statusText}`,
          status: response.status,
        },
        status: response.status,
      };
    }

    const data = await response.json();
    console.log("✅ Session updated successfully:", data);

    return {
      success: true,
      data: data as OnboardingSession,
      status: response.status,
    };
  } catch (error) {
    console.error("❌ Error updating session:", error);
    return {
      success: false,
      data: {
        title: "Update Session Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 500,
    };
  }
}
// ===================================================
// --- IMAGE MANAGEMENT TYPES ---
// ===================================================

export enum HotelImageType {
  Property = 1,
  Room = 2,
}
export interface HotelImageDto {
  imageId: number;
  hotelId: number;
  url: string;
  isCover: boolean;
  caption: string;
  sortOrder: number;
  imageType: number; // 0 = property, 1 = room
}

export interface AddImagesPayload {
  partnerId: number;
  propertyPhotos: {
    url: string;
    isCover: boolean;
    caption: string;
    sortOrder: number;
  }[];
  roomPhotos: {
    url: string;
    isCover: boolean;
    caption: string;
    sortOrder: number;
  }[];
}

export interface DeleteImagesPayload {
  partnerId: number;
  imageIds: number[];
}
// ===================================================
// --- IMAGE MANAGEMENT FUNCTIONS ---
// ===================================================

// GET /api/Hotels/{hotelId}/images
export async function getHotelImages(
  hotelId: number,
  partnerId: number,
  token: string,
): Promise<ApiResponse<HotelImageDto[]>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/images?partnerId=${partnerId}`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel images fetched:", data);
      return {
        success: true,
        data: data as HotelImageDto[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Images API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Hotels/{hotelId}/images
export async function addHotelImages(
  hotelId: number,
  payload: AddImagesPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/images`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      console.log("✅ Images added successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Add Hotel Images API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// DELETE /api/Hotels/{hotelId}/images
export async function deleteHotelImages(
  hotelId: number,
  payload: DeleteImagesPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/images`,
      token,
      {
        method: "DELETE",
        body: JSON.stringify(payload),
      },
    );

    if (response.status === 204 || response.ok) {
      console.log("✅ Images deleted successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Delete Hotel Images API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
// ===================================================
// --- HOTEL AMENITIES AND ACCESS MODES TYPES ---
// ===================================================

export interface AmenityDto {
  id: number;
  code: string;
  name: string;
  selected: boolean;
  node_type: number;
}

export interface AccessModeDto {
  id: number;
  code: string;
  name: string;
  selected: boolean;
  node_type: number;
}

export interface UpdateAmenitiesPayload {
  codes: string[];
}

export interface UpdateAccessModesPayload {
  codes: string[];
}

// ===================================================
// --- HOTEL AMENITIES AND ACCESS MODES FUNCTIONS ---
// ===================================================

// GET /api/Hotels/{hotelId}/amenities
export async function getHotelAmenities(
  hotelId: number,
  token: string,
): Promise<ApiResponse<AmenityDto[]>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/amenities`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel amenities fetched:", data);
      return {
        success: true,
        data: data as AmenityDto[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Amenities API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{hotelId}/amenities
export async function updateHotelAmenities(
  hotelId: number,
  payload: UpdateAmenitiesPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/amenities`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      console.log("✅ Amenities updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Amenities API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/Hotels/{hotelId}/access-modes
export async function getHotelAccessModes(
  hotelId: number,
  token: string,
): Promise<ApiResponse<AccessModeDto[]>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/access-modes`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel access modes fetched:", data);
      return {
        success: true,
        data: data as AccessModeDto[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Access Modes API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{hotelId}/access-modes
export async function updateHotelAccessModes(
  hotelId: number,
  payload: UpdateAccessModesPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/access-modes`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      console.log("✅ Access modes updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Access Modes API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
// ===================================================
// --- PAYMENT METHODS AND CANCELLATION POLICY TYPES ---
// ===================================================

export interface PaymentMethodDto {
  id: number;
  code: string;
  name: string;
  selected: boolean;
}

export interface UpdatePaymentMethodsPayload {
  codes: string[];
}

export interface CancellationPolicyDto {
  policyType: string;
  description: string;
}

export interface UpdateCancellationPolicyPayload {
  policyType: string;
  description: string;
}

// ===================================================
// --- PAYMENT METHODS AND CANCELLATION POLICY FUNCTIONS ---
// ===================================================

// GET /api/Hotels/{hotelId}/payment-methods
export async function getHotelPaymentMethods(
  hotelId: number,
  token: string,
): Promise<ApiResponse<PaymentMethodDto[]>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/payment-methods`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel payment methods fetched:", data);
      return {
        success: true,
        data: data as PaymentMethodDto[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Payment Methods API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{hotelId}/payment-methods
export async function updateHotelPaymentMethods(
  hotelId: number,
  payload: UpdatePaymentMethodsPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/payment-methods`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      console.log("✅ Payment methods updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Payment Methods API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/Hotels/{hotelId}/cancellation-policy
export async function getHotelCancellationPolicy(
  hotelId: number,
  token: string,
): Promise<ApiResponse<CancellationPolicyDto>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/cancellation-policy`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel cancellation policy fetched:", data);
      return {
        success: true,
        data: data as CancellationPolicyDto,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Cancellation Policy API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{hotelId}/cancellation-policy
export async function updateHotelCancellationPolicy(
  hotelId: number,
  payload: UpdateCancellationPolicyPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/cancellation-policy`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      console.log("✅ Cancellation policy updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Cancellation Policy API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// ===================================================
// --- PAYOUT SETTINGS TYPES ---
// ===================================================

export interface PayoutSettingsDto {
  payoutMethodCode: string;
  bankAccountJson: string;
}

export interface BankAccountDetails {
  name: string;
  number: string;
  bank: string;
  branch: string;
  swift?: string;
}

export interface UpdatePayoutSettingsPayload {
  payoutMethodCode: string;
  bankAccount: string; // JSON string of BankAccountDetails
}

// ===================================================
// --- PAYOUT SETTINGS FUNCTIONS ---
// ===================================================

// GET /api/Hotels/{hotelId}/payout-settings
export async function getHotelPayoutSettings(
  hotelId: number,
  token: string,
): Promise<ApiResponse<PayoutSettingsDto>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/payout-settings`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel payout settings fetched:", data);
      return {
        success: true,
        data: data as PayoutSettingsDto,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Payout Settings API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/Hotels/{hotelId}/payout-settings
export async function updateHotelPayoutSettings(
  hotelId: number,
  payload: UpdatePayoutSettingsPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/payout-settings`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Payout settings updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Payout Settings API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// Add this interface with your other interfaces
export interface UpdateHotelBasicInfoPayload {
  name: string;
  description?: string;
  locationId?: number;
  address: string;
  starRating: number;
  phone?: string;
  email?: string;
  website?: string;
  checkinTime: string;
  checkoutTime: string;
  latitude: number;
  longitude: number;
  chainName?: string;
  isPropertyManager: boolean;
  hasBreakfast: boolean;
  country: string;
  city: string;
  province: string;
  zipCode: string;
  partnerId: number;
  parentCategoryId?: number;
  subCategoryId?: number;
  contactFirstName?: string;
  contactLastName?: string;
  contactCountry?: string;
  contactState?: string;
  contactCity?: string;
  contactNationality?: string;
  contactPreferredLanguage?: string;
}

// PUT /api/Hotels/{hotelId}/basic
export async function updateHotelBasicInfo(
  hotelId: number,
  payload: UpdateHotelBasicInfoPayload,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/basic`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Hotel basic info updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Hotel Basic Info API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/Hotels/{hotelId}/basic
export interface HotelBasicInfoResponse {
  name: string;
  description?: string;
  locationId?: number;
  address: string;
  starRating: number;
  phone?: string;
  email?: string;
  website?: string;
  checkinTime: string;
  checkoutTime: string;
  latitude: number;
  longitude: number;
  chainName?: string;
  isPropertyManager: boolean;
  hasBreakfast: boolean;
  country: string;
  city: string;
  province: string;
  zipCode: string;
  partnerId: number;
  parentCategoryId?: number;
  subCategoryId?: number;
  contactFirstName?: string;
  contactLastName?: string;
  contactCountry?: string;
  contactState?: string;
  contactCity?: string;
  contactNationality?: string;
  contactPreferredLanguage?: string;
}

export async function getHotelBasicInfo(
  hotelId: number,
  token: string,
): Promise<ApiResponse<HotelBasicInfoResponse>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/Hotels/${hotelId}/basic`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotel basic info fetched:", data);
      return {
        success: true,
        data: data as HotelBasicInfoResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Hotel Basic Info API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// lib/api.ts

const KEYCLOAK_BASE_URL = "https://keycloak.lilylanka.com";
const KEYCLOAK_REALM = "lilylanka-customer";
const KEYCLOAK_CLIENT_ID = "lilylanka-web";

// --- Partner Keycloak realm ---
const KEYCLOAK_PARTNER_REALM = "lilylanka-partner";
const KEYCLOAK_PARTNER_CLIENT_ID = "lilylanka-partner-web"; // ⚠️ confirm exact client_id with backend dev


export async function loginPartnerWithKeycloak(
  username: string,
  password: string,
): Promise<ApiResponse<KeycloakTokenResponse>> {
  try {
    // Goes through our own /api/partner-auth/token route (not Keycloak
    // directly) so the confidential client's secret stays server-side.
    const response = await fetch("/api/partner-auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ grant_type: "password", username, password }),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as KeycloakTokenResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/**
 * POST /realms/lilylanka-partner/protocol/openid-connect/token (grant_type=refresh_token)
 * Access tokens for this realm/client are short-lived (currently 300s per
 * the token response), so this must run proactively — see
 * schedulePartnerTokenRefresh — not just reactively on a 401.
 */
export async function refreshPartnerKeycloakToken(
  refreshToken: string,
): Promise<ApiResponse<KeycloakTokenResponse>> {
  try {
    const response = await fetch("/api/partner-auth/token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as KeycloakTokenResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

let partnerRefreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Stores the tokens from a Keycloak partner login/refresh response and
 * schedules the next silent refresh ~30s before this access token expires.
 * Call this once right after a successful loginPartnerWithKeycloak, and
 * once on app start if a refreshToken is already in localStorage (so
 * refreshing survives a page reload, not just client-side navigation).
 */
export function persistPartnerTokens(data: KeycloakTokenResponse) {
  localStorage.setItem("partnerAuthToken", data.access_token);
  localStorage.setItem("partnerRefreshToken", data.refresh_token);

  if (partnerRefreshTimer) clearTimeout(partnerRefreshTimer);

  const refreshInMs = Math.max((data.expires_in - 30) * 1000, 5000);
  partnerRefreshTimer = setTimeout(async () => {
    const rt = localStorage.getItem("partnerRefreshToken");
    if (!rt) return;
    const res = await refreshPartnerKeycloakToken(rt);
    if (res.success && res.data) {
      persistPartnerTokens(res.data as KeycloakTokenResponse);
      } else {
      // Refresh token itself expired/invalid (30 min idle) — clear so the
      // next API call's 401 sends the user back to sign in cleanly.
      localStorage.removeItem("partnerAuthToken");
      localStorage.removeItem("partnerRefreshToken");
    }
  }, refreshInMs);
}

/** Call once on app start (e.g. root layout) to resume refreshing after a page reload. */
export function resumePartnerTokenRefresh() {
  const rt = localStorage.getItem("partnerRefreshToken");
  const token = localStorage.getItem("partnerAuthToken");
  if (!rt || !token) return;
  // We don't know remaining lifetime after a reload, so refresh soon rather
  // than trust an unknown remaining TTL.
  persistPartnerTokens({
    access_token: token,
    refresh_token: rt,
    expires_in: 30, // forces a refresh ~in a few seconds via the (expires_in - 30) floor of 5s
  } as KeycloakTokenResponse);
}
// ===================================================
// --- BASE TYPE DEFINITIONS ---
// ===================================================

export interface ApiError {
  title?: string;
  status?: number;
  detail?: string;
  errors?: Record<string, string[]>;
  message?: string;
  error?: string;
}

// export interface ApiResponse<T> {
//   success: boolean;
//   status: number;
//   data?: T | ApiError;
// }

// ===================================================
// --- CUSTOMER AUTH FUNCTIONS ---
// ===================================================

// POST /api/Customer/register
export interface CustomerRegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export interface CustomerRegisterResponse {
  customerId: number;
  identityUserId: string;
}

export async function registerCustomer(
  payload: CustomerRegisterRequest,
): Promise<ApiResponse<CustomerRegisterResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Customer/register`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerRegisterResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Customer Registration API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Customer/send-verification-email
export interface SendVerificationEmailRequest {
  email: string;
}

export interface SendVerificationEmailResponse {
  sent: boolean;
}

export async function sendVerificationEmail(
  payload: SendVerificationEmailRequest,
): Promise<ApiResponse<SendVerificationEmailResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Customer/send-verification-email`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as SendVerificationEmailResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Send Verification Email API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Customer/login
export interface CustomerLoginRequest {
  usernameOrEmail: string;
  password: string;
}

export interface CustomerLoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  tokenType: string;
  scope: string;
}

export async function loginCustomer(
  payload: CustomerLoginRequest,
): Promise<ApiResponse<CustomerLoginResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Customer/login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerLoginResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Customer Login API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Customer/refresh
export interface CustomerRefreshRequest {
  refreshToken: string;
}

export interface CustomerRefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  tokenType: string;
  idToken: string;
  scope: string;
}

export async function refreshCustomerToken(
  refreshToken: string,
): Promise<ApiResponse<CustomerRefreshResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Customer/refresh`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerRefreshResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Customer Token Refresh API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}
// ===================================================
// --- CUSTOMER PASSWORD RESET FUNCTIONS ---
// ===================================================

// POST /api/Customer/forgot-password
export interface CustomerForgotPasswordRequest {
  email: string;
}

export interface CustomerForgotPasswordResponse {
  message: string;
  status: string;
}

export async function customerForgotPassword(
  payload: CustomerForgotPasswordRequest,
): Promise<ApiResponse<CustomerForgotPasswordResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Customer/forgot-password`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerForgotPasswordResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Customer Forgot Password API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/Customer/reset-password
export interface CustomerResetPasswordRequest {
  email: string;
  token: string;
  newPassword: string;
}

export interface CustomerResetPasswordResponse {
  message: string;
  status: string;
}

export async function customerResetPassword(
  payload: CustomerResetPasswordRequest,
): Promise<ApiResponse<CustomerResetPasswordResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Customer/reset-password`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerResetPasswordResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Customer Reset Password API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// ===================================================
// --- KEYCLOAK DIRECT AUTHENTICATION ---
// ===================================================

export interface KeycloakTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_expires_in: number;
  refresh_token: string;
  token_type: string;
  id_token: string;
  "not-before-policy": number;
  session_state: string;
  scope: string;
}

/**
 * Login directly with Keycloak (bypasses backend)
 * Use this if backend login returns 401 but account exists
 */
export async function loginWithKeycloak(
  username: string,
  password: string,
): Promise<ApiResponse<KeycloakTokenResponse>> {
  try {
    const tokenEndpoint = `${KEYCLOAK_BASE_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CLIENT_ID,
      grant_type: "password",
      username: username,
      password: password,
      scope: "openid profile email",
    });

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as KeycloakTokenResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Keycloak Login error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}




/**
 * Refresh Keycloak access token
 */
export async function refreshKeycloakToken(
  refreshToken: string,
): Promise<ApiResponse<KeycloakTokenResponse>> {
  try {
    const tokenEndpoint = `${KEYCLOAK_BASE_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CLIENT_ID,
      grant_type: "refresh_token",
      refresh_token: refreshToken,
    });

    const response = await fetch(tokenEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as KeycloakTokenResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Keycloak Token Refresh error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

/**
 * Logout from Keycloak
 */
export async function logoutFromKeycloak(
  refreshToken: string,
): Promise<boolean> {
  try {
    const logoutEndpoint = `${KEYCLOAK_BASE_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`;

    const params = new URLSearchParams({
      client_id: KEYCLOAK_CLIENT_ID,
      refresh_token: refreshToken,
    });

    const response = await fetch(logoutEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    return response.ok;
  } catch (error) {
    console.error("Keycloak Logout error:", error);
    return false;
  }
}

// ===================================================
// --- EXTERNAL LOGIN (Google via Keycloak) ---
// ===================================================

// Add these constants near the top of api.ts alongside the existing Keycloak constants:
// const KEYCLOAK_CUSTOMER_REALM = "lilylanka-customer";

const KEYCLOAK_CUSTOMER_TOKEN_ENDPOINT = `${KEYCLOAK_BASE_URL}/realms/lilylanka-customer/protocol/openid-connect/token`;

export interface ExternalLoginRequest {
  accessToken: string;
  provider: string;
}

export interface ExternalLoginResponse {
  customerId: number;
  keycloakUserId: string;
  email: string;
  firstName: string;
  lastName: string;
  emailVerified: boolean;
  accessToken: string;
}

/**
 * Exchange a Keycloak authorization code for tokens
 * Called from /auth/callback after Google OAuth redirect
 */
export async function exchangeKeycloakCode(
  code: string,
  redirectUri: string,
): Promise<ApiResponse<KeycloakTokenResponse>> {
  try {
    const params = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: KEYCLOAK_CLIENT_ID,
      redirect_uri: redirectUri,
      code: code,
    });

    const response = await fetch(KEYCLOAK_CUSTOMER_TOKEN_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as KeycloakTokenResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Keycloak code exchange error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/**
 * POST /api/Auth/external-login
 * Exchange a provider access token for a backend session
 */
export async function externalLogin(
  payload: ExternalLoginRequest,
): Promise<ApiResponse<ExternalLoginResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Auth/external-login`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as ExternalLoginResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("External Login API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- CUSTOMER DESTINATIONS FUNCTION ---
// ===================================================

export interface CustomerDestinationsParams {
  q?: string;
  page?: number;
  pageSize?: number;
}

/**
 * GET /api/CustomerDestinations - Search destinations for customers
 */
export async function getCustomerDestinations(
  params?: CustomerDestinationsParams,
): Promise<ApiResponse<DestinationsApiResponse>> {
  try {
    const queryParams = new URLSearchParams();

    if (params?.q) {
      queryParams.append("q", params.q);
    }
    queryParams.append("page", String(params?.page || 1));
    queryParams.append("pageSize", String(params?.pageSize || 10));

    const url = `${API_BASE_URL}/api/CustomerDestinations?${queryParams.toString()}`;
    console.log("🔍 Fetching destinations from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    console.log("📦 Destinations response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Destinations data received:", {
        count: data.destinations?.length || 0,
        total: data.totalCount || 0,
      });

      return {
        success: true,
        data: data as DestinationsApiResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch (e) {
        console.error("❌ Could not parse error response:", e);
      }

      console.error("❌ Destinations API error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Destinations API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}
// ===================================================
// --- GET HOTELS BY DESTINATION ---
// ===================================================

export interface HotelsByDestinationItem {
  hotelId: number;
  hotelName: string;
  locationId: number;
  locationName: string;
  district: string;
  province: string;
  address: string;
  latitude: number;
  longitude: number;
  starRating: number;
  reviewScore: number;
  reviewCount: number;
  coverImageUrl: string;
}

export interface HotelsByDestinationResponse {
  hotels: HotelsByDestinationItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface GetHotelsByDestinationParams {
  page?: number;
  pageSize?: number;
}

/**
 * GET /api/CustomerDestinations/{destinationId}/hotels
 * Optimized endpoint for getting hotels in a specific destination
 */
export async function getHotelsByDestination(
  destinationId: number,
  params?: GetHotelsByDestinationParams,
): Promise<ApiResponse<HotelsByDestinationResponse>> {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("page", String(params?.page || 1));
    queryParams.append("pageSize", String(params?.pageSize || 20));

    const url = `${API_BASE_URL}/api/CustomerDestinations/${destinationId}/hotels?${queryParams.toString()}`;
    console.log("🔍 Fetching hotels by destination from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    console.log("📦 Hotels by destination response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Hotels by destination data received:", {
        count: data.hotels?.length || 0,
        total: data.totalCount || 0,
      });

      return {
        success: true,
        data: data as HotelsByDestinationResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch (e) {
        console.error("❌ Could not parse error response:", e);
      }

      console.error("❌ Hotels by destination API error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Hotels By Destination API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

// ===================================================
// --- LOCATION HIERARCHY (Provinces / Districts / Cities) ---
// ===================================================

export interface LocationHierarchyItem {
  uid: number;
  name: string;
}

// GET /api/locations/provinces
export async function getProvinces(): Promise<
  ApiResponse<LocationHierarchyItem[]>
> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/locations/provinces`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as LocationHierarchyItem[],
        status: response.status,
      };
    }
    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
    };
    try {
      errorData = await response.json();
    } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Provinces API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/locations/districts?provinceId={id}
export async function getDistricts(
  provinceId: number,
): Promise<ApiResponse<LocationHierarchyItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/locations/districts?provinceId=${provinceId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as LocationHierarchyItem[],
        status: response.status,
      };
    }
    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
    };
    try {
      errorData = await response.json();
    } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Districts API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// GET /api/locations/cities?districtId={id}
export async function getCities(
  districtId: number,
): Promise<ApiResponse<LocationHierarchyItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/locations/cities?districtId=${districtId}`,
      {
        method: "GET",
        headers: { Accept: "application/json" },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as LocationHierarchyItem[],
        status: response.status,
      };
    }
    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
    };
    try {
      errorData = await response.json();
    } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Cities API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- END OF FILE ---
// ===================================================

//==================================================
//--- Admin API ---
//==================================================

// ===================================================
// --- CALENDAR TYPES ---
// ===================================================

export interface DateOnly {
  year: number;
  month: number;
  day: number;
  dayOfWeek: number;
}

export interface PriceCalendarUpdate {
  hotelId: number;
  roomTypeId: number;
  ratePlanId: number;
  from: DateOnly;
  toExclusive: DateOnly;
  currency: string;
  price: number;
  priceChild: number;
  allocation: number;
  closed: boolean;
  minStay: number;
  maxStay: number;
  partnerId: number;
}

export interface InventoryCalendarUpdate {
  hotelId: number;
  roomTypeId: number;
  ratePlanId: number;
  from: DateOnly;
  toExclusive: DateOnly;
  soldCount: number;
  holdCount: number;
  blockedCount: number;
  partnerId: number;
}

export interface InventoryCalendarAdjust {
  hotelId: number;
  roomTypeId: number;
  ratePlanId: number;
  from: DateOnly;
  toExclusive: DateOnly;
  soldDelta: number;
  holdDelta: number;
  blockedDelta: number;
  partnerId: number;
}

export interface PriceCalendarDay {
  date: DateOnly;
  currency: string;
  price: number;
  priceChild: number;
  allocation: number;
  closed: boolean;
  minStay: number;
  maxStay: number;
}

export interface InventoryCalendarDay {
  date: DateOnly;
  soldCount: number;
  holdCount: number;
  blockedCount: number;
  available: number;
}

// ===================================================
// --- CALENDAR API FUNCTIONS ---
// ===================================================

// PUT /api/PartnerHotelCalendar/{hotelId} - Bulk upsert prices
export async function updatePriceCalendar(
  hotelId: number,
  payload: PriceCalendarUpdate,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/${hotelId}`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Price calendar updated successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Price Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PATCH /api/PartnerHotelCalendar/{hotelId} - Partial update
export async function patchPriceCalendar(
  hotelId: number,
  payload: Partial<PriceCalendarUpdate>,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/${hotelId}`,
      token,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Price calendar patched successfully");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Patch Price Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/PartnerHotelCalendar/price-calendar
export async function getPriceCalendar(
  hotelId: number,
  params: {
    roomTypeId?: number;
    ratePlanId?: number;
    from: DateOnly;
    toExclusive: DateOnly;
  },
  token: string,
): Promise<ApiResponse<PriceCalendarDay[]>> {
  try {
    const queryParams = new URLSearchParams({
      hotelId: String(hotelId),
    });

    if (params.roomTypeId) {
      queryParams.append("roomTypeId", String(params.roomTypeId));
    }
    if (params.ratePlanId) {
      queryParams.append("ratePlanId", String(params.ratePlanId));
    }

    queryParams.append("from.year", String(params.from.year));
    queryParams.append("from.month", String(params.from.month));
    queryParams.append("from.day", String(params.from.day));
    queryParams.append("toExclusive.year", String(params.toExclusive.year));
    queryParams.append("toExclusive.month", String(params.toExclusive.month));
    queryParams.append("toExclusive.day", String(params.toExclusive.day));

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/price-calendar?${queryParams.toString()}`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Price calendar fetched:", data);
      return {
        success: true,
        data: data as PriceCalendarDay[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Price Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// GET /api/PartnerHotelCalendar/inventory-calendar
export async function getInventoryCalendar(
  hotelId: number,
  params: {
    roomTypeId?: number;
    ratePlanId?: number;
    from: DateOnly;
    toExclusive: DateOnly;
  },
  token: string,
): Promise<ApiResponse<InventoryCalendarDay[]>> {
  try {
    const queryParams = new URLSearchParams({
      hotelId: String(hotelId),
    });

    if (params.roomTypeId) {
      queryParams.append("roomTypeId", String(params.roomTypeId));
    }
    if (params.ratePlanId) {
      queryParams.append("ratePlanId", String(params.ratePlanId));
    }

    queryParams.append("from.year", String(params.from.year));
    queryParams.append("from.month", String(params.from.month));
    queryParams.append("from.day", String(params.from.day));
    queryParams.append("toExclusive.year", String(params.toExclusive.year));
    queryParams.append("toExclusive.month", String(params.toExclusive.month));
    queryParams.append("toExclusive.day", String(params.toExclusive.day));

    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/inventory-calendar?${queryParams.toString()}`,
      token,
      { method: "GET" },
    );

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Inventory calendar fetched:", data);
      return {
        success: true,
        data: data as InventoryCalendarDay[],
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Get Inventory Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// PUT /api/PartnerHotelCalendar/inventory-calendar
export async function updateInventoryCalendar(
  hotelId: number,
  payload: InventoryCalendarUpdate,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/inventory-calendar?hotelId=${hotelId}`,
      token,
      {
        method: "PUT",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Inventory calendar updated");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Update Inventory Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}

// POST /api/PartnerHotelCalendar/inventory-calendar/adjust
export async function adjustInventoryCalendar(
  hotelId: number,
  payload: InventoryCalendarAdjust,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/PartnerHotelCalendar/inventory-calendar/adjust?hotelId=${hotelId}`,
      token,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (response.ok || response.status === 204) {
      console.log("✅ Inventory adjusted");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Adjust Inventory Calendar API error:", error);
    return {
      success: false,
      data: { title: "Network Error" },
      status: 0,
    };
  }
}
// ===================================================
// --- CUSTOMER PROFILE ---
// ===================================================
// GET /api/Customer/me
export interface CustomerMeResponse {
  uid: number;
  keycloakUserId: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
}

export async function getCustomerMe(
  token: string,
): Promise<ApiResponse<CustomerMeResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Customer/me`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const raw = (await response.json()) as Record<string, any>;
      const data: CustomerMeResponse = {
        uid: Number(raw.uid ?? 0),
        keycloakUserId: String(raw.keycloakUserId ?? raw.keycloak_user_id ?? ""),
        firstName: String(raw.firstName ?? raw.first_name ?? ""),
        lastName: String(raw.lastName ?? raw.last_name ?? ""),
        email: String(raw.email ?? ""),
        mobile: String(raw.mobile ?? ""),
      };
      return {
        success: true,
        data,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Customer Me API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// PUT /api/Customer/profile
export interface UpdateCustomerProfilePayload {
  firstName: string;
  lastName: string;
  mobile: string;
}

export async function updateCustomerProfile(
  token: string,
  payload: UpdateCustomerProfilePayload,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Customer/profile`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (response.ok || response.status === 204) {
      return { success: true, status: response.status };
    }

    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
    };
    try {
      errorData = await response.json();
    } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Update Customer Profile API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- SYSTEM ADMIN TOKEN REFRESH ---
// ===================================================

export interface AdminRefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn: number;
  tokenType: string;
  idToken: string;
  scope: string;
}

export async function refreshAdminToken(
  refreshToken: string,
): Promise<ApiResponse<AdminRefreshResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/auth/refresh`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as AdminRefreshResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Admin Token Refresh API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

let adminRefreshTimer: ReturnType<typeof setTimeout> | null = null;

function setAdminCookie(token: string) {
  const expires = new Date(Date.now() + 8 * 60 * 60 * 1000).toUTCString();
  document.cookie = `adminAccessToken=${token}; path=/; expires=${expires}; SameSite=Strict`;
}

/**
 * Stores tokens from admin login/refresh and schedules the next silent
 * refresh ~30s before this access token expires. Call once right after a
 * successful login, and once on app start if a refreshToken already exists
 * in localStorage (so refreshing survives a page reload).
 */
export function persistAdminTokens(data: AdminRefreshResponse) {
  localStorage.setItem("adminAccessToken", data.accessToken);
  localStorage.setItem("adminRefreshToken", data.refreshToken);
  setAdminCookie(data.accessToken);

  if (adminRefreshTimer) clearTimeout(adminRefreshTimer);

  const refreshInMs = Math.max((data.expiresIn - 30) * 1000, 5000);
  adminRefreshTimer = setTimeout(async () => {
    const rt = localStorage.getItem("adminRefreshToken");
    if (!rt) return;
    const res = await refreshAdminToken(rt);
    if (res.success && res.data) {
      persistAdminTokens(res.data as AdminRefreshResponse);
    } else {
      // Refresh token itself expired/invalid — clear so the next API call's
      // 401 sends the admin back to sign in cleanly.
      localStorage.removeItem("adminAccessToken");
      localStorage.removeItem("adminRefreshToken");
      document.cookie =
        "adminAccessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict";
    }
  }, refreshInMs);
}

/** Call once on app start (e.g. admin root layout) to resume refreshing after a reload. */
export function resumeAdminTokenRefresh() {
  const rt = localStorage.getItem("adminRefreshToken");
  const token = localStorage.getItem("adminAccessToken");
  if (!rt || !token) return;
  // Unknown remaining lifetime after reload — refresh soon rather than
  // trust an unknown TTL.
  persistAdminTokens({
    accessToken: token,
    refreshToken: rt,
    expiresIn: 30,
  } as AdminRefreshResponse);
}

/** Clear stored admin tokens and cancel any pending refresh. Call on logout. */
export function clearAdminTokens() {
  if (adminRefreshTimer) {
    clearTimeout(adminRefreshTimer);
    adminRefreshTimer = null;
  }
  localStorage.removeItem("adminAccessToken");
  localStorage.removeItem("adminRefreshToken");
  document.cookie =
    "adminAccessToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict";
}

// ─── ADD THIS to your existing api.ts ───────────────────────────────────────

export interface BookingDetailResult {
  id: number;
  bookingUid: string;
  customerId: number;
  hotelId: number;
  roomTypeId: number;
  ratePlanId: number;
  checkIn: string;
  checkOut: string;
  rooms: number;
  adults: number;
  children: number;
  childrenAges: string;
  currency: string;
  status: string;
  expiresAt: string;
  customerTitle: string;
  customerFirstName: string;
  customerLastName: string;
  customerEmail: string;
  customerPhone: string;
  specialRequests: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
}

export interface BookingDetailResponse {
  result: BookingDetailResult | null;
  message: string;
  errors: ErrorDetail[];
  id: number;
}

export async function getCustomerBookingDetail(
  token: string,
  uid: string,
): Promise<BookingDetailResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/CustomerBookings/${uid}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch booking: ${response.statusText}`);
  }

  return response.json();
}

// ===================================================
// --- CUSTOMER BOOKING PRICING ---
// ===================================================

export interface BookingPricingLineItem {
  code: string;
  name: string;
  amount: number;
  rate: number | null;
}

export interface BookingPricingResponse {
  baseAmount: number;
  taxesTotal: number;
  feesTotal: number;
  discountsTotal: number;
  grossTotal: number;
  platformCommission: number;
  netToHotel: number;
  taxes: BookingPricingLineItem[];
  fees: BookingPricingLineItem[];
  discounts: BookingPricingLineItem[];
}

/** GET /api/CustomerBookings/{bookingId}/pricing */
export async function getCustomerBookingPricing(
  bookingId: number,
  token: string,
): Promise<ApiResponse<BookingPricingResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/CustomerBookings/${bookingId}/pricing`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as BookingPricingResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
    };
    try {
      errorData = await response.json();
    } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Booking Pricing error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// --- CUSTOMER BOOKINGS LIST ---

export interface CustomerBookingListItem {
  id: number;
  booking_Uid: string;
  hotel_Name: string;
  check_in: string;
  check_out: string;
  bookingstatus: string;
  totalRecordCount: number;
}

export interface CustomerBookingsListResponse {
  items: CustomerBookingListItem[];
  paging: {
    results: number;
    total: number;
    nextOffset: string;
  } | null;
  pagingView: {
    results: number;
    total: number;
    pageNo: number;
    pageSize: number;
  } | null;
}

export interface GetCustomerBookingsParams {
  offset?: number;
  count?: number;
  Customer_Uid?: string; // Maps to KeycloakUserId
  searchKeyword?: string;
  sortColumn?: string;
  sortOrder?: string;
}

/**
 * GET /api/CustomerBookings - Get all bookings for the current customer
 */
export async function getCustomerBookings(
  token: string,
  params?: GetCustomerBookingsParams,
): Promise<ApiResponse<CustomerBookingsListResponse>> {
  try {
    const queryParams = new URLSearchParams();

    // Map internal 'offset' and 'count' to API 'Offset' and 'Count'
    if (params?.offset !== undefined) {
      queryParams.append("Offset", String(params.offset));
    }
    if (params?.count !== undefined) {
      queryParams.append("Count", String(params.count));
    }

    // Map Customer_Uid to KeycloakUserId if that's what the endpoint uses
    if (params?.Customer_Uid) {
      queryParams.append("KeycloakUserId", String(params.Customer_Uid));
    }

    if (params?.sortColumn) queryParams.append("sortColumn", params.sortColumn);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);
    if (params?.searchKeyword) queryParams.append("searchKeyword", params.searchKeyword);

    const url = `${API_BASE_URL}/api/CustomerBookings?${queryParams.toString()}`;
    console.log("🔍 Fetching customer bookings from:", url);

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      
      // Fixed the logging to match the actual JSON structure you provided
      console.log("✅ Customer bookings data received:", {
        itemCount: data.CustomerBookings?.length || 0,
        total: data.paging?.total || 0,
      });

      return {
        success: true,
        data: data as CustomerBookingsListResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {
        /* ignore parse error */
      }
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("❌ Get Customer Bookings API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}



// ===================================================
// --- CUSTOMER REVIEWS ---
// ===================================================

export interface CustomerReview {
  uid?: number;
  id?: number;
  hotelName?: string;
  hotelId?: number;
  location?: string;
  rating?: number;
  comment?: string;
  reviewText?: string;
  createdAt?: string;
  [key: string]: any;
}

export interface CustomerReviewsResponse {
  items: CustomerReview[];
}

export async function getCustomerReviews(
  token: string,
): Promise<ApiResponse<CustomerReviewsResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/CustomerReview`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerReviewsResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Get Customer Reviews API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- WISHLIST TYPES ---
// ===================================================

export interface WishlistResponse {
  items: string[]; // Array of hotel UIDs as strings
}

export interface AddToWishlistResponse {
  wishlistUid: number;
  hotelUid: number;
}

// ===================================================
// --- WISHLIST FUNCTIONS ---
// ===================================================

/**
 * GET /api/Wishlist - Get user's wishlist
 */
export async function getWishlist(
  token: string,
): Promise<ApiResponse<WishlistResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/Wishlist`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    console.log("📦 Wishlist response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Wishlist data received:", data);
      return {
        success: true,
        data: data as WishlistResponse,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      console.error("❌ Wishlist API error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Get Wishlist API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

/**
 * POST /api/Wishlist/hotels/{hotelUid} - Add hotel to wishlist
 */
export async function addToWishlist(
  hotelUid: number,
  token: string,
): Promise<ApiResponse<AddToWishlistResponse>> {
  try {
    console.log("🔍 Adding to wishlist:", {
      hotelUid,
      tokenPreview: token.substring(0, 20) + "...", // ✅ Log first 20 chars
      url: `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`,
    });
    const response = await fetch(
      `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    console.log("📦 Response:", {
      status: response.status,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries()),
    });

    console.log("📦 Add to wishlist response status:", response.status);

    if (response.ok) {
      const data = await response.json();
      console.log("✅ Added to wishlist:", data);
      return {
        success: true,
        data: data as AddToWishlistResponse,
        status: response.status,
      };
   } else {
      const responseText = await response.text();
      console.error("❌ Add to wishlist failed:", {
        status: response.status,
        statusText: response.statusText,
        body: responseText,
      });
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
        detail: responseText,
      };
      try {
        errorData = JSON.parse(responseText);
      } catch {}
      console.error("❌ Add to wishlist error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Add to Wishlist API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

/**
 * DELETE /api/Wishlist/hotels/{hotelUid} - Remove hotel from wishlist
 */
export async function removeFromWishlist(
  hotelUid: number,
  token: string,
): Promise<ApiResponse<void>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Wishlist/hotels/${hotelUid}`,
      {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    console.log("📦 Remove from wishlist response status:", response.status);

    if (response.ok) {
      console.log("✅ Removed from wishlist");
      return {
        success: true,
        status: response.status,
      };
    } else {
      let errorData: ApiError = {
        title: response.statusText,
        status: response.status,
      };
      try {
        errorData = await response.json();
      } catch {}
      console.error("❌ Remove from wishlist error:", errorData);
      return {
        success: false,
        data: errorData,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("❌ Remove from Wishlist API error:", error);
    return {
      success: false,
      data: {
        title: "Network Error",
        detail: error instanceof Error ? error.message : "Unknown error",
      },
      status: 0,
    };
  }
}

/**
 * Check if a hotel is in the wishlist
 */
export async function isHotelInWishlist(
  hotelUid: number,
  token: string,
): Promise<boolean> {
  try {
    const response = await getWishlist(token);
    if (response.success && response.data) {
      const wishlistData = response.data as WishlistResponse;
      return wishlistData.items.includes(hotelUid.toString());
    }
    return false;
  } catch (error) {
    console.error("Error checking wishlist status:", error);
    return false;
  }
}

// ===================================================
// --- DELETE CUSTOMER ACCOUNT ---
// ===================================================

export async function deleteCustomerAccount(
  token: string,
): Promise<ApiResponse<void>> {
  try {
    console.log("🗑️ Attempting delete with token length:", token.length);

    const response = await fetch(`${API_BASE_URL}/api/Customer/me`, {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });

    console.log("🗑️ Delete response status:", response.status);

    if (response.ok || response.status === 200 || response.status === 204) {
      return { success: true, status: response.status };
    }

    const responseText = await response.text();
    console.error("🗑️ Delete error body:", responseText);

    let errorData: ApiError = {
      title: response.statusText,
      status: response.status,
      detail: responseText,
    };
    try { errorData = JSON.parse(responseText); } catch {}
    return { success: false, data: errorData, status: response.status };

  } catch (error) {
    console.error("Delete Customer Account API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- CONTACT FORM FUNCTIONS ---
// ===================================================

export interface ContactFormRequest {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

export interface ContactFormData {
  uid: number;
  creationDate: string;
}

export interface ContactFormResponse {
  errors: ErrorDetail[];
  hasErrors: boolean;
  data: ContactFormData | null;
}

export interface ContactFormRequest {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
}

export interface ContactFormData {
  uid: number;
  creationDate: string;
}

export interface ContactFormResponse {
  errors: ErrorDetail[];
  hasErrors: boolean;
  data: ContactFormData | null;
}

export async function sendContactForm(
  payload: ContactFormRequest,
): Promise<ContactFormResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/contact/messages`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.statusText}`);
  }

  return response.json();
}

// ===================================================
// --- TOUR PACKAGE BOOKINGS (CUSTOMER) ---
// ===================================================

export interface TourPackageBookingItem {
  bookingUid: string;
  bookingRef: string;
  packageId: string;
  slotId: string;
  startDate: string;
  endDate: string;
  adults: number;
  children: number;
  totalPax: number;
  subtotalLkr: number;
  insuranceTotalLkr: number;
  grandTotalLkr: number;
  bookingStatus: string;
  paymentStatus: string;
  specialRequests: string;
  createdAt: string;
}

export async function getCustomerTourPackageBookings(
  token: string,
  customerId: number,
): Promise<ApiResponse<TourPackageBookingItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/tour-package-bookings?customerId=${customerId}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as TourPackageBookingItem[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Tour Package Bookings error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface TourCustomRequestItem {
  requestUid: string;
  packageId: string;
  requestedStartDate: string;
  requestedEndDate: string;
  requestedStartTime: string | null;
  requestedEndTime: string | null;
  pax: number;
  notes: string;
  status: string;
  createdAt: string;
}

export async function getCustomerTourCustomRequests(
  token: string,
  customerId: number,
): Promise<ApiResponse<TourCustomRequestItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/tour-package-bookings/custom-requests?customerId=${customerId}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as TourCustomRequestItem[], status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Tour Custom Requests error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- NEWSLETTER ---
// ===================================================

export interface NewsletterSubscribeResponse {
  uid: number;
  email: string;
  inserted: boolean;
  message: string;
}

export async function subscribeNewsletter(
  email: string,
): Promise<ApiResponse<NewsletterSubscribeResponse>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/newsletter/subscribe`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as NewsletterSubscribeResponse, status: response.status };
    } else {
      let errorData: ApiError = { title: response.statusText, status: response.status };
      try { errorData = await response.json(); } catch {}
      return { success: false, data: errorData, status: response.status };
    }
  } catch (error) {
    console.error("Newsletter Subscribe API error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- PUBLIC NEWS TYPES ---
// ===================================================

export interface NewsListItemPublic {
  uid: number;
  title: string;
  slug: string;
  summary: string;
  category: string;
  isFeatured: boolean;
  publishedAt: string;
  coverImageUrl: string;
  coverImageAltText: string;
}

export interface NewsListResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  items: NewsListItemPublic[];
}

export interface NewsImagePublic {
  uid: number;
  newsArticleId: number;
  imageUrl: string;
  caption: string;
  altText: string;
  sortOrder: number;
  isCover: boolean;
}

export interface NewsDetailPublic {
  uid: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  category: string;
  isFeatured: boolean;
  publishedAt: string;
  metaTitle: string;
  metaDescription: string;
  images: NewsImagePublic[];
  videos: { videoUrl: string; title: string; sortOrder: number }[];
}

// ===================================================
// --- PUBLIC NEWS FUNCTIONS ---
// ===================================================

export async function getPublicNews(params?: {
  page?: number;
  pageSize?: number;
  category?: string;
  q?: string;
}): Promise<ApiResponse<NewsListResponse>> {
  try {
    const query = new URLSearchParams({
      page: String(params?.page ?? 1),
      pageSize: String(params?.pageSize ?? 9),
    });
    if (params?.category) query.set("category", params.category);
    if (params?.q) query.set("q", params.q);

    const response = await fetch(`${API_BASE_URL}/api/news?${query}`, {
      headers: { Accept: "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as NewsListResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Public News error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export async function getPublicNewsBySlug(
  slug: string,
): Promise<ApiResponse<NewsDetailPublic>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/news/${slug}`, {
      headers: { Accept: "application/json" },
    });

    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as NewsDetailPublic, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get News By Slug error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- EXPERIENCE BOOKING PAYMENTS (CUSTOMER) ---
// ===================================================

export interface CustomerPaymentLinkItem {
  paymentId: number;
  transactionId: number;
  bookingUid: string;
  paymentType: string;
  amount: number;
  currency: string;
  paymentStatus: number;
  paymentStatusText: string;
  paycenterReqId: string;
  paymentPageUrl: string;
  createdAt: string;
}

/** GET /api/customer/experience-bookings/{bookingUid}/payment-links */
export async function getCustomerExperiencePaymentLinks(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<CustomerPaymentLinkItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/experience-bookings/${bookingUid}/payment-links`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerPaymentLinkItem[],
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Experience Payment Links error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface ExperiencePaymentReturnResponse {
  bookingUid: string;
  paymentId: number;
  transactionId: number;
  paymentStatus: number;
  gatewayResponseCode: string;
  gatewayResponseText: string;
  txnReference: string;
}

/** GET /api/customer/experience-booking-payments/return */
export async function getExperiencePaymentReturn(
  params: { bookingUid: string; paymentId: number; transactionId: number },
  token: string,
): Promise<ApiResponse<ExperiencePaymentReturnResponse>> {
  try {
    const query = new URLSearchParams({
      bookingUid: params.bookingUid,
      paymentId: String(params.paymentId),
      transactionId: String(params.transactionId),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/customer/experience-booking-payments/return?${query}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as ExperiencePaymentReturnResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Experience Payment Return error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface FinalizeExperiencePaymentPayload {
  bookingUid: string;
  paymentId: number;
  transactionId: number;
  outcomeHint?: number;
}

/** POST /api/customer/experience-booking-payments/finalize */
export async function finalizeExperiencePayment(
  payload: FinalizeExperiencePaymentPayload,
  token: string,
): Promise<ApiResponse<ExperiencePaymentReturnResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/experience-booking-payments/finalize`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as ExperiencePaymentReturnResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Finalize Experience Payment error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- CUSTOMER EXPERIENCE BOOKINGS (paginated) ---
// ===================================================

export interface CustomerExperienceBookingItem {
  bookingUid: string;
  bookingRef: string;
  experienceId: string;
  slotId: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  adults: number;
  children: number;
  totalPax: number;
  subtotalLkr: number;
  insuranceTotalLkr: number;
  grandTotalLkr: number;
  bookingStatus: string;
  paymentStatus: string;
  specialRequests?: string;
  creationDate: string;
}

export interface CustomerExperienceBookingsResponse {
  page: number;
  pageSize: number;
  totalCount: number;
  items: CustomerExperienceBookingItem[];
}

/** GET /api/customer/experience-bookings */
export async function getCustomerExperienceBookingsList(
  token: string,
  params?: { page?: number; pageSize?: number },
): Promise<ApiResponse<CustomerExperienceBookingsResponse>> {
  try {
    const query = new URLSearchParams({
      page: String(params?.page ?? 1),
      pageSize: String(params?.pageSize ?? 20),
    });
 const response = await fetch(
      `${API_BASE_URL}/api/customer/experience-bookings?${query}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerExperienceBookingsResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Experience Bookings error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/** GET /api/customer/experience-bookings/{bookingUid} */
export async function getCustomerExperienceBookingDetail(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<CustomerExperienceBookingItem>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/experience-bookings/${bookingUid}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerExperienceBookingItem,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Experience Booking Detail error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- TOUR PACKAGE BOOKING PAYMENTS (CUSTOMER) ---
// ===================================================

export interface TourPackagePaymentLinkItem {
  paymentId: number;
  transactionId: number;
  bookingUid: string;
  paymentType: string;
  amount: number;
  currency: string;
  paymentStatus: number;
  paymentStatusText: string;
  paycenterReqId: string;
  paymentPageUrl: string;
  createdAt: string;
}

/** GET /api/customer/tour-package-bookings/{bookingUid}/payment-links */
export async function getCustomerTourPackagePaymentLinks(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<TourPackagePaymentLinkItem[]>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/tour-package-bookings/${bookingUid}/payment-links`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as TourPackagePaymentLinkItem[],
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Tour Package Payment Links error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface TourPackagePaymentReturnResponse {
  bookingUid: string;
  paymentId: number;
  transactionId: number;
  paymentStatus: number;
  gatewayResponseCode: string;
  gatewayResponseText: string;
  txnReference: string;
}

/** GET /api/customer/tour-package-booking-payments/return */
export async function getTourPackagePaymentReturn(
  params: { bookingUid: string; paymentId: number; transactionId: number },
  token: string,
): Promise<ApiResponse<TourPackagePaymentReturnResponse>> {
  try {
    const query = new URLSearchParams({
      bookingUid: params.bookingUid,
      paymentId: String(params.paymentId),
      transactionId: String(params.transactionId),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/customer/tour-package-booking-payments/return?${query}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as TourPackagePaymentReturnResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Tour Package Payment Return error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface FinalizeTourPackagePaymentPayload {
  bookingUid: string;
  paymentId: number;
  transactionId: number;
  outcomeHint?: number;
}

/** POST /api/customer/tour-package-booking-payments/finalize */
export async function finalizeTourPackagePayment(
  payload: FinalizeTourPackagePaymentPayload,
  token: string,
): Promise<ApiResponse<TourPackagePaymentReturnResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/tour-package-booking-payments/finalize`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as TourPackagePaymentReturnResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Finalize Tour Package Payment error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- CUSTOMER TRANSPORT BOOKINGS ---
// Swagger only documents "Success" with no response schema for these
// endpoints, so the shapes below are inferred from the admin-side
// transport-booking endpoints (same underlying booking entity). Confirm
// field names against a real response the first time each screen is used,
// and adjust here rather than in the components.
// ===================================================

// Raw shape of a single booking — mirrors ApiTransportBookingListItem from
// the admin side, since customer/admin almost certainly read the same
// transport_booking row.
export interface CustomerTransportBookingItem {
  uid: string;
  bookingReference: string;
  vehicleUid: string | null;
  partnerId: number | null;
  customRequestUid: string | null;
  serviceType: string; // "DAILY_RENTAL" | "PACKAGE" | "AIRPORT_TRANSFER" | "CUSTOM"
  pricingSourceType: string; // "DAILY" | "PACKAGE" | "AIRPORT" | "CUSTOM_QUOTE"
  pricingSourceUid: string | null;
  status: string;
  paymentStatus: string;
  currency: string;
  estimatedAmount: number | null;
  quotedAmount: number | null;
  confirmedAmount: number | null;
  pickupDatetime: string | null;
  returnDatetime: string | null;
  pickupAddress: string | null;
  destinationAddress: string | null;
  returnAddress: string | null;
  destinationSummary: string | null;
  driverOption: string | null;
  creationDate: string;
}

export interface CustomerTransportBookingsResponse {
  items: CustomerTransportBookingItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** GET /api/customer/transport/bookings */
export async function getCustomerTransportBookings(
  token: string,
  params?: { page?: number; pageSize?: number },
): Promise<ApiResponse<CustomerTransportBookingsResponse>> {
  try {
    const query = new URLSearchParams({
      page: String(params?.page ?? 1),
      pageSize: String(params?.pageSize ?? 20),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings?${query}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerTransportBookingsResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Bookings error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// Quote fields are a best guess (mirrors the shape confirm/quote-creation
// implies on the admin side) — verify against a real quote once one exists.
export interface CustomerTransportQuote {
  uid: string;
  bookingUid: string;
  totalAmountLkr: number;
  currency: string;
  validUntil: string;
  customerMessage: string | null;
  status: string; // e.g. "PENDING" | "ACCEPTED" | "REJECTED" | "EXPIRED"
  creationDate: string;
  [key: string]: unknown;
}

export type CustomerTransportQuoteItem = Record<string, unknown>;

export interface CustomerTransportBookingHistoryItem {
  uid: string;
  bookingUid: string;
  fromStatus: string | null;
  toStatus: string;
  changedBy: number | null;
  changedByType: string | null;
  comment: string | null;
  creationDate: string | null;
}

export type CustomerTransportBookingMessage = Record<string, unknown>;

// Envelope GET /api/customer/transport/bookings/{bookingUid} is assumed to
// return, mirroring the admin detail endpoint's shape.
export interface CustomerTransportBookingDetailResponse {
  booking: CustomerTransportBookingItem;
  quotes: CustomerTransportQuote[];
  quoteItems: CustomerTransportQuoteItem[];
  history: CustomerTransportBookingHistoryItem[];
  messages: CustomerTransportBookingMessage[];
}

/** GET /api/customer/transport/bookings/{bookingUid} */
export async function getCustomerTransportBookingDetail(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<CustomerTransportBookingDetailResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}`,
      {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerTransportBookingDetailResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Booking Detail error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface CancelTransportBookingPayload {
  reason: string;
}

/** PATCH /api/customer/transport/bookings/{bookingUid}/cancel */
export async function cancelCustomerTransportBooking(
  bookingUid: string,
  payload: CancelTransportBookingPayload,
  token: string,
): Promise<ApiResponse<CustomerTransportBookingDetailResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}/cancel`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerTransportBookingDetailResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Cancel Customer Transport Booking error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface AcceptTransportQuotePayload {
  quoteUid: string;
}

/** PATCH /api/customer/transport/bookings/{bookingUid}/accept-quote */
export async function acceptCustomerTransportQuote(
  bookingUid: string,
  payload: AcceptTransportQuotePayload,
  token: string,
): Promise<ApiResponse<CustomerTransportBookingDetailResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}/accept-quote`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerTransportBookingDetailResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Accept Customer Transport Quote error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface RejectTransportQuotePayload {
  quoteUid: string;
  reason: string;
}

/** PATCH /api/customer/transport/bookings/{bookingUid}/reject-quote */
export async function rejectCustomerTransportQuote(
  bookingUid: string,
  payload: RejectTransportQuotePayload,
  token: string,
): Promise<ApiResponse<CustomerTransportBookingDetailResponse>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}/reject-quote`,
      {
        method: "PATCH",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );
    if (response.ok) {
      const data = await response.json();
      return {
        success: true,
        data: data as CustomerTransportBookingDetailResponse,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Reject Customer Transport Quote error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

// ===================================================
// --- CUSTOMER TRANSPORT PAYMENTS ---
// ===================================================

export interface CustomerTransportPaymentAttempt {
  uid: string;
  paymentUid: string;
  attemptNumber: number;
  gatewayName: string;
  gatewayRequestId: string | null;
  gatewayTransactionId: string | null;
  paymentUrl: string | null;
  status: string; // "REDIRECTED" | "SUCCESS" | "FAILED" | ...
  expiresAt: string | null;
  creationDate: string;
}

// Matches the real GET /api/customer/transport/bookings/{bookingUid}/payment
// response, confirmed against a live payload. POST /payment-attempts is
// assumed to return the same shape — verify the first time it's called
// against a booking with no existing payment record.
export interface CustomerTransportPaymentItem {
  uid: string;
  paymentReference: string;
  bookingUid: string;
  bookingReference?: string | null;
  customerId?: number | null;
  amount: number;
  paidAmount?: number | null;
  currency: string;
  status: string; // "PROCESSING" | "PAID" | "FAILED" | "CANCELLED" | "EXPIRED"
  paymentMethod?: string | null;
  gatewayName?: string | null;
  gatewayTransactionId?: string | null;
  description?: string | null;
  paymentUrl?: string | null;
  expiresAt?: string | null;
  paidAt?: string | null;
  receiptUrl?: string | null;
  creationDate?: string | null;
  attempts?: CustomerTransportPaymentAttempt[];
  [key: string]: unknown;
}

export interface CustomerTransportPaymentsResponse {
  items: CustomerTransportPaymentItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** GET /api/customer/transport/bookings/{bookingUid}/payment */
export async function getCustomerTransportBookingPayment(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<CustomerTransportPaymentItem>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}/payment`,
      {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as CustomerTransportPaymentItem, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Booking Payment error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/** GET /api/customer/transport/payments */
export async function getCustomerTransportPayments(
  token: string,
  params?: { page?: number; pageSize?: number },
): Promise<ApiResponse<CustomerTransportPaymentsResponse>> {
  try {
    const query = new URLSearchParams({
      page: String(params?.page ?? 1),
      pageSize: String(params?.pageSize ?? 20),
    });
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/payments?${query}`,
      {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as CustomerTransportPaymentsResponse, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Payments error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/** GET /api/customer/transport/payments/{paymentUid} */
export async function getCustomerTransportPaymentById(
  paymentUid: string,
  token: string,
): Promise<ApiResponse<CustomerTransportPaymentItem>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/payments/${paymentUid}`,
      {
        method: "GET",
        cache: "no-store",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as CustomerTransportPaymentItem, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Payment By Id error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/** POST /api/customer/transport/bookings/{bookingUid}/payment-attempts */
export async function createCustomerTransportPaymentAttempt(
  bookingUid: string,
  token: string,
): Promise<ApiResponse<CustomerTransportPaymentItem>> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/bookings/${bookingUid}/payment-attempts`,
      {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      },
    );
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as CustomerTransportPaymentItem, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Create Customer Transport Payment Attempt error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

/**
 * POST /api/customer/transport/payments/{paymentUid}/attempts/{attemptUid}/finalize
 * Explicitly resolves a payment attempt's status (PAID/FAILED/CANCELLED)
 * from the browser's own return redirect. This exists because the backend's
 * server-to-server webhook handler for PayCenter callbacks currently throws
 * ("Missing responseData in Paycenter PAYMENT_COMPLETE response") and never
 * updates the payment row — so without calling this, the payment is stuck
 * at PROCESSING forever regardless of what actually happened at the gateway.
 */
export async function finalizeCustomerTransportPayment(
  paymentUid: string,
  attemptUid: string,
  token: string,
  cancelled: boolean = false,
): Promise<ApiResponse<CustomerTransportPaymentItem>> {
  try {
    const query = new URLSearchParams({ cancelled: String(cancelled) });
    const response = await fetch(
      `${API_BASE_URL}/api/customer/transport/payments/${paymentUid}/attempts/${attemptUid}/finalize?${query}`,
      {
        method: "POST",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      },
    );
    if (response.ok) {
      let data: unknown = null;
      try {
        data = await response.json();
      } catch {
        // Some finalize responses may return 200 with no body.
      }
      return {
        success: true,
        data: (data ?? undefined) as CustomerTransportPaymentItem,
        status: response.status,
      };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Finalize Customer Transport Payment error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}

export interface CustomerTransportDashboard {
  [key: string]: unknown;
}

/** GET /api/customer/transport/dashboard */
export async function getCustomerTransportDashboard(
  token: string,
): Promise<ApiResponse<CustomerTransportDashboard>> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/customer/transport/dashboard`, {
      method: "GET",
      cache: "no-store",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
    });
    if (response.ok) {
      const data = await response.json();
      return { success: true, data: data as CustomerTransportDashboard, status: response.status };
    }
    let errorData: ApiError = { title: response.statusText, status: response.status };
    try { errorData = await response.json(); } catch {}
    return { success: false, data: errorData, status: response.status };
  } catch (error) {
    console.error("Get Customer Transport Dashboard error:", error);
    return { success: false, data: { title: "Network Error" }, status: 0 };
  }
}